import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { CBadge, CButton, CSpinner } from '@coreui/react';
import CIcon from '@coreui/icons-react';
import dayjs from 'dayjs';
import { callTokenApi } from '../../../../../../apiCaller';
import { toast } from 'react-toastify';
import { capitalizeFirstLetter } from '../../../../../../utils';
import { API_AI_INSIGHTS_JOB_BY_ID, API_AI_INSIGHTS_JOB_RERUN, API_AI_INSIGHTS_JOB_RERUNS } from '../../../../../../constants/apiRest';
import ViewMarkdown from '../Components/ViewMarkdown';
import Accordion from '../../../../../general/accordion';
import { subscribe, unsubscribe } from '../../../../../../containers/subscriber/eventBus';
import { TRIGGER_TYPE } from '../../../../../../constants/aiInsights';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

const ReviewAnalyseDetail = ({ insight }) => {
    const { id: accountId } = useSelector((state) => state.subscriber.activeAccount);
    const [loading, setLoading] = useState(false);
    const [jobChain, setJobChain] = useState([]);
    const [expandedJobId, setExpandedJobId] = useState(null);

    useEffect(() => {
        if (insight?.id) {
            fetchJobChain(insight.id);
        }
    }, [insight]);

    // Fetch single job detail by ID
    const fetchJobById = async (jobId) => {
        try {
            const response = await callTokenApi(API_AI_INSIGHTS_JOB_BY_ID.replace(':id', jobId), 'GET');
            if (response?.data?.code === 200) {
                return response.data.data;
            }
        } catch {
            // Silent fail
        }
        return null;
    };

    // Listen for pusher event to update job status in realtime
    useEffect(() => {
        const handleJobStatusUpdate = async (data) => {
            if (!data?.jobId) return;

            // Check if the updated job is in our job chain
            const jobIndex = jobChain.findIndex((job) => job.id === data.jobId);
            if (jobIndex !== -1) {
                // If job is done or failed, fetch full job data to get generatedInsightMarkdown
                if (data.status === 'done' || data.status === 'failed') {
                    const fullJobData = await fetchJobById(data.jobId);
                    if (fullJobData) {
                        setJobChain((prevChain) => {
                            const newChain = [...prevChain];
                            newChain[jobIndex] = fullJobData;
                            return newChain;
                        });
                        return;
                    }
                }

                // Otherwise just update status
                setJobChain((prevChain) => {
                    const newChain = [...prevChain];
                    newChain[jobIndex] = {
                        ...newChain[jobIndex],
                        status: data.status,
                        completedAt: data.completedAt,
                        errorMessage: data.errorMessage,
                    };
                    return newChain;
                });
            }
        };

        subscribe('ai-insight-job-status-update', handleJobStatusUpdate);

        return () => {
            unsubscribe('ai-insight-job-status-update', handleJobStatusUpdate);
        };
    }, [jobChain]);

    const fetchJobChain = async (id) => {
        setLoading(true);
        try {
            const response = await callTokenApi(API_AI_INSIGHTS_JOB_RERUNS.replace(':jobId', id), 'GET');

            if (response?.data?.code === 200) {
                const chain = response.data.data || [];
                setJobChain(chain);
                // Default to expanding the latest job (last in array since sorted by rerunNumber asc)
                if (chain.length > 0) {
                    setExpandedJobId(chain[chain.length - 1].id);
                }
            } else {
                toast.error('Failed to load insight details');
            }
        } catch {
            toast.error('An error occurred while loading insight details');
        } finally {
            setLoading(false);
        }
    };

    // Rerun insight directly with old prompt
    const handleRerunInsight = async () => {
        if (!insight) return;

        if (!insight.templateId) {
            toast.error('Template ID not found for this insight');
            return;
        }

        const response = await callTokenApi(
            API_AI_INSIGHTS_JOB_RERUN.replace(':jobId', insight.id),
            'POST',
            { accountId }
        );

        if (response?.data?.code === 200) {
            toast.success('Insight re-run started successfully');

            // Add new job to chain immediately without reload
            const { jobId, rerunNumber, parentJobId } = response.data.data || {};
            if (jobId) {
                const newJob = {
                    id: jobId,
                    parentJobId,
                    rerunNumber,
                    status: 'pending',
                    createdAt: new Date().toISOString(),
                    templateName: displayData?.templateName,
                    scheduleName: displayData?.scheduleName,
                };
                setJobChain((prevChain) => [...prevChain, newJob]);
                setExpandedJobId(jobId);
            }
        } else {
            toast.error(response?.data?.message || 'Failed to re-run insight');
        }
    };

    const formatDate = (dateString) => {
        return dateString ? dayjs.utc(dateString).format('MMM D, YYYY') : 'N/A';
    };

    // Get status badge color
    const getStatusColor = (status) => {
        if (!status) return 'secondary';

        switch (status) {
            case 'done':
                return 'success';
            case 'pending':
                return 'warning';
            case 'waiting':
                return 'info';
            case 'failed':
                return 'danger';
            default:
                return 'secondary';
        }
    };

    // Format email recipients as a comma-separated string
    const formatRecipients = (recipients) => {
        if (!recipients || recipients.length === 0) return 'None';

        // Handle string format by parsing to JSON
        if (typeof recipients === 'string') {
            try {
                recipients = JSON.parse(recipients);
            } catch (e) {
                return recipients;
            }
        }

        // Handle array of objects with label and value properties
        if (Array.isArray(recipients) && recipients.length > 0) {
            if (recipients[0] && (recipients[0].label || recipients[0].value)) {
                return recipients.map((recipient) => recipient.label || recipient.value).join(', ');
            }
            // Handle simple array of strings
            return recipients.join(', ');
        }

        // Fallback for any other format
        return String(recipients);
    };

    // Use the first job in chain (original) for display metadata, fallback to insight prop
    const displayData = jobChain.length > 0 ? jobChain[0] : insight;

    if (loading) {
        return (
            <div className='rule-detail account-connected-detail review-analyse'>
                <div className='cvr-create-new text-center py-5'>
                    <CSpinner color='primary' />
                    <p className='mt-3'>Loading insight details...</p>
                </div>
            </div>
        );
    }

    if (!displayData) {
        return (
            <div className='rule-detail account-connected-detail review-analyse'>
                <div className='cvr-create-new text-center py-5'>
                    <p>No insight data available.</p>
                </div>
            </div>
        );
    }

    return (
        <div className='rule-detail account-connected-detail review-analyse'>
            <div className='cvr-create-new'>
                <div className='d-flex align-items-center justify-content-between mb-3'>
                    <h1 className='mb-0'>AI Insights Analysis</h1>
                    {insight?.dateRangeJson && insight?.triggerType !== TRIGGER_TYPE.MANUAL_PREVIEW && (
                        <CButton
                            color='primary'
                            variant='outline'
                            size='sm'
                            onClick={handleRerunInsight}
                            disabled={displayData?.status === 'pending' || displayData?.status === 'waiting'}
                        >
                            <CIcon icon='iconReload' height={14} className='mr-2' />
                            Re-run Insight
                        </CButton>
                    )}
                </div>

                <ul className='d-flex flex-column gap-1 list-unstyled my-4'>
                    <li className='d-flex gap-2'>
                        <strong>AI Insights Template:</strong>
                        <span>{displayData.templateName || ''}</span>
                    </li>
                    <li className='d-flex gap-2'>
                        <strong>Schedule:</strong>
                        <span>{displayData.scheduleName || ''}</span>
                    </li>
                    
                    <li className='d-flex gap-2'>
                        <strong>Generated on:</strong>
                        <span>{formatDate(displayData.createdAt)}</span>
                    </li>

                    <li className='d-flex gap-2'>
                        <strong>Completed on:</strong>
                        <span>{formatDate(displayData.completedAt)}</span>
                    </li>

                    <li className='d-flex gap-2'>
                        <strong>Status:</strong>
                        <span>{capitalizeFirstLetter(displayData.status) || ''}</span>
                    </li>

                    {displayData.emailSubject && (
                        <li className="d-flex gap-2">
                            <strong>Subject:</strong>
                            <span>{capitalizeFirstLetter(displayData.emailSubject)}</span>
                        </li>
                    )}

                    <li className='d-flex gap-2'>
                        <strong>Email to:</strong>
                        <span>{formatRecipients(displayData.emailedRecipients)}</span>
                    </li>

                    <li className='d-flex gap-2'>
                        <strong>Email Status:</strong>
                        <span>{capitalizeFirstLetter(displayData.emailDeliveryStatus) || 'Not sent'}</span>
                    </li>
                </ul>

                <div className='insight-content'>
                    {/* Job Chain Accordion - show only if there are jobs */}
                    {jobChain.length > 0 ? (
                        <div className='job-chain-accordion'>
                            {jobChain.map((job) => {
                                const jobLabel = !job.parentJobId ? 'Original' : `Rerun ${job.rerunNumber}`;
                                const jobDate = formatDate(job.completedAt || job.createdAt);
                                const jobStatus = job.status ? capitalizeFirstLetter(job.status) : '';

                                return (
                                    <div key={job.id} className='accordion'>
                                        <Accordion
                                            accordionType={job.id}
                                            title={
                                                <div>
                                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                                        <span className="mr-3">{jobLabel}</span>
                                                        <CBadge color={getStatusColor(job.status)}>{jobStatus || 'Unknown'}</CBadge>
                                                    </div>
                                                    <div className="d-flex align-items-center">
                                                        <span className="text-muted small">Completed: {jobDate}</span>
                                                    </div>
                                                </div>
                                            }
                                            description=""
                                            accordionShow={expandedJobId}
                                            setAccordionShow={setExpandedJobId}
                                            className='job-chain-item'
                                        >
                                            {job.errorMessage ? (
                                                <ViewMarkdown markdown={job.errorMessage} />
                                            ) : job.generatedInsightMarkdown ? (
                                                <ViewMarkdown markdown={job.generatedInsightMarkdown} />
                                            ) : (
                                                <p className='text-muted mb-0'>No content available for this insight.</p>
                                            )}
                                        </Accordion>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        // Fallback to displayData when no job chain
                        displayData.errorMessage ? (
                            <ViewMarkdown markdown={displayData.errorMessage} />
                        ) : displayData.generatedInsightMarkdown ? (
                            <ViewMarkdown markdown={displayData.generatedInsightMarkdown} />
                        ) : (
                            <p className='text-muted'>No content available for this insight.</p>
                        )
                    )}
                </div>
            </div>

        </div>
    );
};

ReviewAnalyseDetail.propTypes = {
    insight: PropTypes.shape({
        dateRangeJson: PropTypes.any,
        triggerType: PropTypes.any,
    }),
};

export default ReviewAnalyseDetail;
